import React from 'react';
import { Button, SparkIcon, CardSurface } from '@spark';
import { INFERENCE_CHART_OCI_REF, INFERENCE_VALUES_TEMPLATE_URL } from '@/shared/constants/registry';
import CodeBlockWithSelector from './CodeBlockWithSelector';
import LoggingCodeBlock from './LoggingCodeBlock';
import HighlightedCode from './HighlightedCode';
import { installationCode, specificAICode } from './codeExamples';
import { renderInstallationCode } from './codeRenderers';
import './Documentation.css';

const InstallationDoc = () => {
    const codeVariants = {
        python: installationCode.python,
        typescript: installationCode.typescript,
        go: installationCode.go,
    };

    return (
        <div className="fs-unmask">
            <h1 className="fs-unmask docs-section-title">Installing the SDK</h1>
            <p className="fs-unmask docs-section-subtitle">
                Use the specific_ai SDK to interact with your deployed SpecificAI models.
                Optionally, you can trace and log these interactions.
            </p>
            <h3>Installation</h3>
            <div className="docs-code-card">
                <CodeBlockWithSelector
                    codeVariants={codeVariants}
                    renderCode={(language) => renderInstallationCode(language)}
                    languages={['python', 'typescript', 'go']}
                    showLanguageSelector={true}
                    showProviderSelector={false}
                    showDownloadButton={false}
                    defaultLanguage="python"
                />
            </div>
        </div>
    );
};

const QuickStartDoc = () => (
    <div>
        <h1 className="docs-section-title">Quick Start</h1>
        <CardSurface bordered padding="medium">
            <div className="docs-body-text">
                <p>
                    To get started quickly with the SDK, check out the{' '}
                    <a href="#example-usage" className="docs-link">Example Usage</a>{' '}
                    section for complete code examples in Python and TypeScript.
                </p>
                <p>Here&apos;s a minimal example:</p>
                <ol className="docs-ordered-list">
                    <li><strong>Install the SDK</strong> - See the <a href="#installation" className="docs-link">Installation</a> section</li>
                    <li><strong>Initialize the client</strong> with your SpecificAI platform URL</li>
                    <li><strong>Send a request</strong> to your deployed model</li>
                    <li><strong>Process the response</strong></li>
                </ol>
            </div>
        </CardSurface>
    </div>
);

const ConfigurationDoc = () => (
    <div>
        <h1 className="docs-section-title">Configuration</h1>
        <CardSurface bordered padding="medium">
            <h3>OpenAIHttpClient Configuration Options</h3>
            <ul className="docs-body-text">
                <li><strong>URL</strong>: OpenAI API endpoint (default: https://api.openai.com/v1/chat/completions)</li>
                <li><strong>optuneURL</strong>: Your Optune service URL</li>
                <li><strong>apiKey</strong>: Your OpenAI API key</li>
                <li><strong>modelName</strong>: The model to use (e.g., &quot;gpt-3.5-turbo&quot;)</li>
                <li><strong>useOptuneInference</strong>: Enable/disable Optune&apos;s optimized models</li>
            </ul>

            <h3 style={{ marginTop: 20 }}>Request Options</h3>
            <ul className="docs-body-text">
                <li><strong>usecaseName</strong>: Identifier for your specific use case</li>
                <li><strong>additionalOptions</strong>: Optional OpenAI parameters (temperature, max_tokens, etc.)</li>
                <li><strong>headers</strong>: Optional custom headers for the request</li>
            </ul>
        </CardSurface>
    </div>
);

const QueryingCodeBlock = () => {
    const baseUrl = `${window.location.origin}/`;
    const codeVariants = {
        python: specificAICode.python(baseUrl),
        typescript: specificAICode.typescript(baseUrl),
        go: specificAICode.go(baseUrl),
    };

    return (
        <CodeBlockWithSelector
            codeVariants={codeVariants}
            languages={['python', 'typescript', 'go']}
            codeLanguage={{ python: 'python', typescript: 'typescript', go: 'go' }}
            showLanguageSelector={true}
            showProviderSelector={false}
            defaultLanguage="python"
        />
    );
};

const QueryingDoc = () => (
    <div>
        <h1 className="fs-unmask docs-section-title">Querying</h1>
        <p className="fs-unmask docs-section-subtitle">
            Use the specific_ai SDK to interact with your deployed SpecificAI models.
            Optionally, you can trace and log these interactions into the SpecificAI platform.
        </p>
        <div className="docs-code-card">
            <QueryingCodeBlock />
        </div>
    </div>
);

const LoggingDoc = () => (
    <div>
        <h1 className="fs-unmask docs-section-title">Logging</h1>
        <p className="fs-unmask docs-section-subtitle">
            Log interactions from external (non-SpecificAI) models into the platform.
            Choose a language and task type to see the corresponding script. Records
            are added to an existing Task or used to bootstrap a new one with the same
            task and project name.
        </p>
        <div className="docs-code-card">
            <LoggingCodeBlock />
        </div>
    </div>
);

const HELM_INSTALL_EXAMPLE =
    `helm upgrade --install <RELEASE_NAME> \\\n` +
    `  ${INFERENCE_CHART_OCI_REF} \\\n` +
    `  --version 0.1.0 \\\n` +
    `  -n specificai-inference --create-namespace \\\n` +
    `  -f my-model-values.yaml`;

const ECR_LOGIN_EXAMPLE =
    `aws ecr get-login-password --region us-west-2 \\\n` +
    `  | helm registry login --username AWS --password-stdin \\\n` +
    `    390402537632.dkr.ecr.us-west-2.amazonaws.com`;

const FlowStepHeading = ({ number, title }) => (
    <div className="docs-step-heading">
        <span className="docs-step-badge">{number}</span>
        <h3 style={{ margin: 0 }}>{title}</h3>
    </div>
);

const ExportFormatsTable = () => (
    <table className="docs-table">
        <thead>
            <tr>
                <th>Format</th>
                <th>You get</th>
                <th>Best for</th>
            </tr>
        </thead>
        <tbody>
            <tr>
                <td><strong>Model raw files (ZIP)</strong></td>
                <td>Packaged model files</td>
                <td>Custom integrations</td>
            </tr>
            <tr>
                <td><strong>Docker image</strong></td>
                <td><code>.tar</code> inference image</td>
                <td><code>docker load</code> on your own host</td>
            </tr>
            <tr>
                <td><strong>Push to ECR</strong></td>
                <td>Image in your AWS ECR repo</td>
                <td>Helm deploy on Kubernetes</td>
            </tr>
            <tr>
                <td><strong>Push to GCR</strong></td>
                <td>Image in your GCP Artifact Registry / GCR</td>
                <td>Helm deploy on Kubernetes (GCP)</td>
            </tr>
        </tbody>
    </table>
);

const ExternalDeployFlowCard = () => (
    <CardSurface bordered padding="medium" style={{ marginBottom: 24 }}>
        <h3 style={{ marginTop: 0 }}>Export a model and deploy externally</h3>
        <p className="docs-paragraph--spaced docs-paragraph">
            Start on the <strong>Models</strong> page. Use the same Configure deployment step
            for every format; only the final action differs (save to disk vs push to ECR / GCR).
        </p>

        <FlowStepHeading number={1} title="Choose a format and configure" />
        <p className="docs-paragraph">
            <strong>Models</strong> → model drawer → <strong>Download model</strong> → pick a format → <strong>Next</strong>.
        </p>
        <ExportFormatsTable />
        <div className="docs-paragraph--spaced docs-paragraph">
            In <strong>Configure deployment</strong> set cloud provider, instance type, and device
            (summarization needs <strong>GPU</strong>). For Triton tasks, set prediction strategy and instances if shown.
            <ul className="docs-list">
                <li><strong>ZIP / Docker</strong> — click <strong>Done</strong>, wait for the progress bar, then <strong>Save to disk</strong>.</li>
                <li><strong>Push to ECR</strong> — pick an <strong>ECR target</strong>, click <strong>Push to ECR</strong>, wait for <strong>Image pushed to ECR</strong>. Note the repository URI and tag.</li>
                <li><strong>Push to GCR</strong> — pick a <strong>GCR target</strong> (Account Settings → GCP - GCR), click <strong>Push to GCR</strong>, wait for <strong>Image pushed to GCR</strong>.</li>
            </ul>
        </div>

        <FlowStepHeading number={2} title="Image tag (ECR / GCR push)" />
        <p className="docs-paragraph--spaced docs-paragraph">
            Tags are set automatically: <code>{'{project-name}-{task-name}-v{version}'}</code>
            <br />
            Example: <code>acme-corp-shutdown-reporter-test-v1</code>
        </p>

        <FlowStepHeading number={3} title="Get the Helm values file" />
        <p className="docs-paragraph">
            Use the public template (one file for Triton and vLLM — set <code>runtime</code> inside):
        </p>
        <a href={INFERENCE_VALUES_TEMPLATE_URL} target="_blank" rel="noopener noreferrer">
            <Button
                variant="primary"
                size="medium"
                startIcon={<SparkIcon slug="external-link-icons" size="small" />}
                style={{ marginBottom: 16 }}
            >
                Open values template on GitHub
            </Button>
        </a>
        <div className="docs-paragraph--spaced docs-paragraph">
            Copy it locally (e.g. <code>my-model-values.yaml</code>) and edit at minimum:
            <ul className="docs-list">
                <li><code>runtime</code> — <code>triton</code> (classification, NER, …) or <code>vllm</code> (summarization)</li>
                <li><code>image.repository</code> — your ECR URI without tag</li>
                <li><code>image.tag</code> — tag from Step 1</li>
                <li><code>nodeSelector</code> / <code>tolerations</code> — your GPU node pool</li>
                <li><code>ingress.host</code> and <code>ingress.path</code> — if exposing HTTP(S)</li>
            </ul>
        </div>

        <FlowStepHeading number={4} title="Install the Helm chart" />
        <p className="docs-paragraph">
            Log in to the chart registry (once per shell), then install with your values file:
        </p>
        <HighlightedCode code={ECR_LOGIN_EXAMPLE} language="bash" />
        <div className="docs-spacer" />
        <HighlightedCode code={HELM_INSTALL_EXAMPLE} language="bash" />
        <div className="docs-paragraph" style={{ marginTop: 16 }}>
            When the pod is <code>Ready</code>, check health:
            <ul className="docs-list">
                <li>Triton: <code>/v2/health/ready</code></li>
                <li>vLLM: <code>/health</code></li>
            </ul>
        </div>
    </CardSurface>
);

const ExternalDeployDoc = () => (
    <div className="fs-unmask">
        <h1 className="fs-unmask docs-section-title">External Deploy</h1>
        <p className="fs-unmask docs-section-subtitle">
            Export a trained model from the platform (ZIP, Docker tar, ECR, or GCR push), then deploy it
            to your Kubernetes cluster with the public SpecificAI inference Helm chart.
            The engineering catalog lists this Helm install as a third path alongside in-platform Deploy
            and download/push; the full format list (including Docker Hub and Azure ACR) is in
            <code>docs/features/model-export-and-deploy.md</code>.
        </p>
        <p className="docs-note">
            <strong>Before you start:</strong> for ECR push, confirm{' '}
            <strong>same-account</strong> targets have platform IRSA with ECR push access, or{' '}
            <strong>cross-account</strong> targets have the assume-role configured in Account Settings.
            For <strong>GCP - GCR</strong>, configure Artifact Registry targets (and a service-account /
            WIF JSON for customer projects) under Account Settings.
            Need help?{' '}
            <a href="mailto:support@specific.ai" className="docs-link">
                support@specific.ai
            </a>
        </p>
        <ExternalDeployFlowCard />
    </div>
);

const SupportDoc = () => (
    <div>
        <h1 className="fs-unmask docs-section-title">Support</h1>
        <p className="fs-unmask docs-section-subtitle">
            Need help? Contact our support team at{' '}
            <a href="mailto:support@specific.ai" className="docs-link">
                support@specific.ai
            </a>
        </p>
    </div>
);

const SECTION_COMPONENTS = {
    'quick-start': QuickStartDoc,
    'docs-configuration': ConfigurationDoc,
    'querying': QueryingDoc,
    'logging': LoggingDoc,
    'external-deploy': ExternalDeployDoc,
    'support-doc': SupportDoc,
};

const DOCS_PAGE_SECTIONS = ['installation', 'querying', 'logging', 'external-deploy', 'support-doc'];

export function Documentation({ section }) {
    const SectionComponent = SECTION_COMPONENTS[section] || InstallationDoc;
    const isDocsPage = DOCS_PAGE_SECTIONS.includes(section);

    return (
        <div className={`docs-page${isDocsPage ? '' : ' docs-page--inline'}`}>
            <div className="docs-content">
                <SectionComponent />
            </div>
        </div>
    );
}
